// Authentication service for managing user authentication state
import { api } from './api'

export interface User {
  user_id: number
  name: string
  company_name?: string
  nif: string
  email?: string
  role: string
  source_address: string
  vehicle_type: string
  vehicle_year?: number
  license_plate?: string
  created_at: string
  // Subscription fields
  stripe_customer_id?: string
  subscription_status: 'active' | 'canceled' | 'past_due' | 'trial' | 'incomplete' | 'free'
  subscription_end_date?: string
  plan_id?: string
}

export interface LoginData {
  nif: string
  password: string
}

export interface RegisterData {
  name: string
  company_name?: string
  nif: string
  email?: string
  password: string
  source_address: string
  source_lat: number
  source_lng: number
  vehicle_type: string
  vehicle_year?: number
  license_plate?: string
}

export interface AuthResponse {
  message: string
  user: User
  token: string
  timestamp: string
  cache_warning?: string
}

class AuthService {
  private readonly TOKEN_KEY = 'auth_token'
  private readonly USER_KEY = 'user_data'
  private readonly LOGIN_TIMESTAMP_KEY = 'login_timestamp'

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken()
    const user = this.getUser()
    return !!(token && user)
  }

  /**
   * Check if user has active subscription
   */
  hasActiveSubscription(): boolean {
    const user = this.getUser()
    if (!user) return false
    
    const activeStatuses = ['active', 'trial', 'past_due']
    return activeStatuses.includes(user.subscription_status)
  }

  /**
   * Check if user can access premium features
   * Note: Free users can access route generation (with 1 free report limit checked by backend)
   */
  canAccessPremiumFeatures(): boolean {
    const user = this.getUser()
    if (!user) return false
    
    // All users (including free) can access route generation
    // Backend handles the 1 free report limit for free tier users
    // Allow access for free, active, trial, and past_due (grace period)
    const allowedStatuses = ['free', 'active', 'trial', 'past_due']
    return user.subscription_status ? allowedStatuses.includes(user.subscription_status) : false
  }

  /**
   * Get subscription display status
   */
  getSubscriptionDisplayStatus(): string {
    const user = this.getUser()
    if (!user) return 'Não autenticado'
    
    const statusMap: Record<string, string> = {
      'active': 'Ativa',
      'trial': 'Período de Teste',
      'past_due': 'Em Atraso',
      'canceled': 'Cancelada',
      'incomplete': 'Incompleta',
      'free': 'Gratuita'
    }
    
    return statusMap[user.subscription_status] || user.subscription_status
  }

  /**
   * Get current authentication token
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY)
  }

  /**
   * Get current user data
   */
  getUser(): User | null {
    const userData = localStorage.getItem(this.USER_KEY)
    if (!userData) return null

    try {
      return JSON.parse(userData) as User
    } catch (error) {
      console.error('Failed to parse user data:', error)
      this.clearAuthData()
      return null
    }
  }

  /**
   * Get login timestamp
   */
  getLoginTimestamp(): Date | null {
    const timestamp = localStorage.getItem(this.LOGIN_TIMESTAMP_KEY)
    if (!timestamp) return null

    try {
      return new Date(timestamp)
    } catch (error) {
      console.error('Failed to parse login timestamp:', error)
      return null
    }
  }

  /**
   * Login user with NIF and password
   */
  async login(data: LoginData): Promise<AuthResponse> {
    try {
      const response = await api.post('/api/auth/login', data)
      const authData = response as AuthResponse
      this.storeAuthData(authData)
      return authData
    } catch (error) {
      console.error('Login error:', error)
      throw error
    }
  }

  /**
   * Register new user
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      const response = await api.post('/api/v1/auth/register', data)
      const authData = response as AuthResponse
      this.storeAuthData(authData)
      return authData
    } catch (error) {
      console.error('Registration error:', error)
      throw error
    }
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      // Call backend logout endpoint (optional, as it's not fully implemented)
      const token = this.getToken()
      if (token) {
        await api.post('/api/auth/logout', {})
      }
    } catch (error) {
      console.error('Logout API call failed:', error)
      // Continue with local logout even if API call fails
    } finally {
      // Always clear local authentication data
      this.clearAuthData()
    }
  }

  /**
   * Store authentication data in localStorage
   */
  private storeAuthData(authData: AuthResponse): void {
    try {
      localStorage.setItem(this.TOKEN_KEY, authData.token)
      localStorage.setItem(this.USER_KEY, JSON.stringify(authData.user))
      localStorage.setItem(this.LOGIN_TIMESTAMP_KEY, new Date().toISOString())
    } catch (error) {
      console.error('Failed to store auth data:', error)
      throw new Error('Failed to store authentication data')
    }
  }

  /**
   * Clear all authentication data
   */
  clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY)
    localStorage.removeItem(this.USER_KEY)
    localStorage.removeItem(this.LOGIN_TIMESTAMP_KEY)
  }

  /**
   * Check if token is expired (basic check based on timestamp)
   */
  isTokenExpired(): boolean {
    const loginTime = this.getLoginTimestamp()
    if (!loginTime) return true

    // Token expires after 24 hours (this should match backend JWT expiration)
    const expirationTime = new Date(loginTime.getTime() + 24 * 60 * 60 * 1000)
    return new Date() > expirationTime
  }

  /**
   * Get authorization header for API requests
   */
  getAuthHeader(): Record<string, string> {
    const token = this.getToken()
    if (!token) return {}

    return {
      Authorization: `Bearer ${token}`
    }
  }

  /**
   * Refresh token (placeholder - not implemented in backend yet)
   */
  async refreshToken(): Promise<void> {
    try {
      const response = await api.post('/api/auth/refresh', {})
      
      // Update token if refresh is successful
      const newToken = (response as any).token
      if (newToken) {
        localStorage.setItem(this.TOKEN_KEY, newToken)
      }
    } catch (error) {
      console.error('Token refresh failed:', error)
      // If refresh fails, clear auth data and redirect to login
      this.clearAuthData()
      throw error
    }
  }

  /**
   * Refresh user data from backend (e.g., after subscription changes)
   */
  async refreshUserData(): Promise<void> {
    try {
      // Get current user data from backend using the current user's ID
      const currentUser = this.getUser()
      if (!currentUser) {
        throw new Error('No current user found')
      }

      // Fetch fresh user data from backend
      const response = await api.get('/api/users/me') as { user: User }
      
      if (response && response.user) {
        // Update localStorage with fresh user data
        localStorage.setItem(this.USER_KEY, JSON.stringify(response.user))
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error)
      
      // Fallback: try to get subscription status only
      try {
        const currentUser = this.getUser()
        if (currentUser) {
          const subResponse = await api.get('/api/stripe/subscription/status') as {
            status: string
            plan_name?: string
            current_period_end?: number
            cancel_at_period_end: boolean
          }
          
          if (subResponse) {
            const updatedUser = {
              ...currentUser,
              subscription_status: subResponse.status || 'free',
              plan_id: subResponse.plan_name
            }
            localStorage.setItem(this.USER_KEY, JSON.stringify(updatedUser))
          }
        }
      } catch (fallbackError) {
        console.error('Fallback refresh also failed:', fallbackError)
        throw error
      }
    }
  }

  /**
   * Setup automatic token refresh
   */
  setupTokenRefresh(): void {
    // Check token expiration every 30 minutes
    setInterval(() => {
      if (this.isAuthenticated() && this.isTokenExpired()) {
        console.log('Token expired, attempting refresh...')
        this.refreshToken().catch(() => {
          console.log('Token refresh failed, logging out user')
          this.logout()
          // Redirect to login page
          window.location.href = '/login'
        })
      }
    }, 30 * 60 * 1000) // 30 minutes
  }

  /**
   * Initialize auth service
   */
  init(): void {
    // Clean up expired sessions on initialization
    if (this.isAuthenticated() && this.isTokenExpired()) {
      console.log('Session expired on init, clearing auth data')
      this.clearAuthData()
    }

    // Setup automatic token refresh
    this.setupTokenRefresh()
  }
}

// Export singleton instance
export const authService = new AuthService()

// Initialize on module load
authService.init()
